import { useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Button, IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow, Typography
} from '@mui/material';
import {
  BlockOutlined, DashboardOutlined
} from '@mui/icons-material';
import TemplateDialog from './Modal/TemplateDialog';

const diagnosisRows = [
  { code: 'A37-90', diagnosis: 'Pertussis' },
  { code: 'E039', diagnosis: 'Hypothyroidism' },
  { code: 'D64-9', diagnosis: 'Anemia, Unspecified' }
];

const cptRows = [
  { code: '11750', cpt: 'Nail removal with matrix' },
  { code: '11200', cpt: 'Skin tag, 1-15' },
  { code: '16000', cpt: 'Burn care, First degree, Initial' }
];

const panelSx = {
  borderRadius: 0,
  boxShadow: 'none',
  bgcolor: '#fff',
  width: '100%'
};

const sectionHeaderSx = {
  height: 52,
 p: 1.5,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: '1px solid #DADADA',
  bgcolor: '#fff'
};

const titleSx = {
  fontSize: 14,
  fontWeight: 500,
  color: '#424242'
};

const headCellSx = {
  height: 40,
  padding: '0 18px',
  fontSize: '12px',
  fontWeight: 500,
  color: '#424242',
  backgroundColor: '#FAFAFA',
  borderBottom: '1px solid #E0E0E0',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap'
};

const bodyCellSx = {
  height: 40,
  padding: '0 18px',
  fontSize: '13px',
  color: '#424242',
  borderBottom: '1px solid #EEEEEE'
};


const codeCellSx = {
  ...bodyCellSx,
  fontWeight: 500
};

const templateButtonSx = {
  // fontSize: 12,
  fontWeight: 500,
  color: '#424242',
  borderRadius: '4px',
  textTransform: 'uppercase',
  borderColor: '#BBD7EE',
  bgcolor: '#EAF6FF',
  '&:hover': {
    bgcolor: '#DDF0FC',
    borderColor: '#9FC7E6'
  },
  '& svg': {
    fontSize: 16
  }
};

const bodyRowSx = {
  '&:hover': {
    bgcolor: '#FCF8F2',

    '& .MuiTableCell-root': {
      bgcolor: '#FCF8F2'
    }
  }
};

const cptTemplateButtonSx = {
  ...templateButtonSx,
  borderColor: '#EFDCB5',
  bgcolor: '#F8F1E2',
  '&:hover': {
    bgcolor: '#FFEBC5',
    borderColor: '#D9BD85'
  }
};

const actionIconSx = {
  p: 0.85,
  color: '#757575',

  '& svg': {
    fontSize: 18
  }
};

const addRowInputSx = {
  '& .MuiOutlinedInput-root': {
    height: 36,
    fontSize: 13,
    borderRadius: '3px'
  }
};

const dialogPaperSx = {
  width: '95%',
  maxWidth: 1200,
  borderRadius: '4px',
  overflow: 'hidden'
};

const dialogHeaderSx = {
  height: 48,
  px: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: '1px solid #E0E0E0',
  bgcolor: '#fff'
};

const DataSection = ({
  title,
  buttonText,
  rows,
  type,
  isAdd,
  onAdd,
  onTemplateOpen
}) => {
  const isCpt = type === 'cpt';

  return (
    <Box>
      <Box sx={sectionHeaderSx}>
        <Typography sx={titleSx}>{title}</Typography>

        <Button
          variant="outlined"
          size="small"
          startIcon={<DashboardOutlined />}
          sx={isCpt ? cptTemplateButtonSx : templateButtonSx}
          // onClick={() => onTemplateOpen(isCpt ? 'cpt' : 'diagnosis')}
          >
          {buttonText}
        </Button>
      </Box>

      <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headCellSx, width: '42%' }}>CODE</TableCell>

            <TableCell sx={headCellSx}>{isCpt ? 'CPT' : 'Diagnosis'}</TableCell>

            <TableCell align="center" sx={{ ...headCellSx, width: 180 }}>
              ACTIONS
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map(row => (
            <TableRow key={row.code} sx={bodyRowSx}>
              <TableCell sx={codeCellSx}>{row.code}</TableCell>

              <TableCell sx={bodyCellSx}>
                {isCpt ? row.cpt : row.diagnosis}
              </TableCell>

              <TableCell align="center" sx={bodyCellSx}>
                <IconButton size="small" sx={actionIconSx}>
                  <BlockOutlined />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const DxUpdate = () => {
  const [isAddDiagnosis, setIsAddDiagnosis] = useState(false);
  const [isAddCpt, setIsAddCpt] = useState(false);

  const [dialog, setDialog] = useState({
    open: false,
    type: 'diagnosis'
  });

  const handleTemplateOpen = type => {
    setDialog({
      open: true,
      type
    });
  };

  const handleDialogClose = () => {
    setDialog(prev => ({
      ...prev,
      open: false
    }));
  };

  return (
    <>
      <Paper sx={panelSx}>
        <DataSection
          title="Patient Diagnosis"
          buttonText="Diagnosis Template"
          rows={diagnosisRows}
          type="diagnosis"
          isAdd={isAddDiagnosis}
          onAdd={() => setIsAddDiagnosis(prev => !prev)}
          onTemplateOpen={handleTemplateOpen}
        />

        <DataSection
          title="Patient CPT"
          buttonText="CPT Template"
          rows={cptRows}
          type="cpt"
          isAdd={isAddCpt}
          onAdd={() => setIsAddCpt(prev => !prev)}
          onTemplateOpen={handleTemplateOpen}
        />

        <Box sx={{ height: 80, bgcolor: '#fff' }} />
      </Paper>

      <TemplateDialog
        open={dialog.open}
        type={dialog.type}
        onClose={handleDialogClose}
      />
    </>
  );
};

DxUpdate.propTypes = {
  className: PropTypes.string
};

export default DxUpdate;
